import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Download, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, errMsg, downloadFile } from "../../lib/api";

const ResultBox = ({ result }) => (
  <div className="border p-3 text-sm space-y-1" data-testid="import-result">
    <div className="flex items-center gap-2 font-medium text-emerald-700">
      <CheckCircle2 className="h-4 w-4" /> {result.imported} baris berhasil diimpor
    </div>
    {result.skipped > 0 && <div className="text-amber-700">{result.skipped} baris dilewati:</div>}
    {result.errors.map((e) => <div key={e} className="text-xs text-muted-foreground">{e}</div>)}
  </div>
);

export const ImportDialog = ({ open, cat, meta, user, defaultPolda, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [polda, setPolda] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const isPolda = user.role === "polda";

  useEffect(() => {
    if (open) {
      setFile(null);
      setResult(null);
      setPolda(defaultPolda || "");
    }
  }, [open, defaultPolda]);

  const template = () => downloadFile(`/template/${cat.key}`, {}, `template_${cat.key}.xlsx`).catch(() => toast.error("Gagal mengunduh template"));

  const upload = async () => {
    if (!file) return toast.error("Pilih file Excel terlebih dahulu");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/${cat.key}`, fd, { params: !isPolda && polda ? { polda } : {} });
      setResult(data);
      if (data.imported) {
        toast.success(`${data.imported} baris diimpor`);
        onDone();
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Impor Excel &mdash; {cat.label}</DialogTitle>
          <DialogDescription>
            {isPolda ? `Data akan ditambahkan ke ${user.polda}.` : "Data akan ditambahkan ke Polda pada kolom POLDA, atau Polda yang dipilih di bawah."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between border p-3 bg-muted/40">
            <div className="text-sm"><div className="font-medium">1. Unduh template</div><div className="text-xs text-muted-foreground">Isi data mulai baris ke-2, jangan ubah judul kolom.</div></div>
            <Button variant="outline" size="sm" onClick={template} data-testid="download-template-button"><Download className="h-4 w-4" /> Template</Button>
          </div>
          {!isPolda && (
            <div className="space-y-1.5">
              <Label>Polda (jika kolom POLDA kosong)</Label>
              <Select value={polda} onValueChange={setPolda}>
                <SelectTrigger data-testid="import-polda-select"><SelectValue placeholder="Pilih Polda" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {meta.polda_list.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <label className="flex flex-col items-center justify-center gap-2 border border-dashed p-6 cursor-pointer hover:bg-muted/40 transition-colors">
            <FileSpreadsheet className="h-6 w-6 text-muted-foreground" />
            <span className="text-sm font-medium" data-testid="import-file-name">{file ? file.name : "2. Pilih file .xlsx"}</span>
            <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="import-file-input" />
          </label>
          {result && <ResultBox result={result} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="import-close-button">Tutup</Button>
          <Button onClick={upload} disabled={busy || !file} data-testid="import-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Unggah & Impor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
